import React, { useState, useEffect, useMemo, useRef, useCallback, SyntheticEvent } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { MaybePromise } from '../types';
import { Validator } from './useInput';

type UseMultiSelectArgs<ItemType> = {
  name?: string;
  initialValue?: ItemType[];
  validator?: Validator<ItemType[]>;
  validate?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  onChange?(e: SyntheticEvent, eventData?: { name?: string; values: ItemType[] }): void;
  prepareItems(values: string): MaybePromise<ItemType[] | null | undefined>;
  renderValue(item: ItemType): string;
  renderItem(item: ItemType): React.ReactNode;
};

type UseMultiSelect<ItemType> = {
  name?: string;
  values: ItemType[];
  items: ItemType[];
  isInvalid?: boolean;
  errorMessage?: string;
  setValues(values: ItemType[]): void;
  renderValue(item: ItemType): string;
  renderItem(item: ItemType): React.ReactNode;
  onInputChange(e: any, eventData: { value: string }): void;
  onChange(e: React.SyntheticEvent<Element, Event>, eventData: { values: ItemType[] }): void;
  clear(): void;
  refresh(): void;
};

export function useMultiSelect<T extends object, ItemType>({
  name,
  initialValue,
  validator,
  validate,
  isInvalid: isExternalInvalid,
  errorMessage: externalErrorMessage,
  onChange: onChangeCallback,
  prepareItems,
  renderValue,
  renderItem,
  ...rest
}: T & UseMultiSelectArgs<ItemType>): Omit<T, keyof UseMultiSelectArgs<ItemType>> &
  UseMultiSelect<ItemType> {
  const [values, setValues] = useState<ItemType[]>(initialValue || []);
  const [items, setItems] = useState<ItemType[]>([]);
  const isInvalidRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    initialValue?.length && setValues(initialValue);
  }, [initialValue]);

  const fetchItems = useDebouncedCallback(async (val: string) => {
    const data = await prepareItems(val);
    if (data) {
      setItems(data);
      setIsLoading(false);
    }
  }, 300);

  // load all data after start
  useEffect(() => {
    fetchItems('');
  }, [fetchItems]);

  const onInputChange = (e: React.ChangeEvent, eventData: { value: string }) => {
    fetchItems(eventData.value);
  };

  const onChange = useCallback(
    (e: React.SyntheticEvent, eventData: { values: ItemType[] }) => {
      const map = new Map<string, ItemType>();
      for (const value of eventData.values) {
        map.set(renderValue(value), value);
      }
      // remove duplicates
      const values = Array.from(map.values());
      onChangeCallback?.(e, { ...eventData, values });

      setValues([...map.values()]);
    },
    [onChangeCallback, renderValue],
  );

  const clear = useCallback(() => {
    setValues([]);
  }, []);

  const refresh = useCallback(() => {
    fetchItems('');
  }, [fetchItems]);

  const validationResult = useMemo(() => {
    if (validate || isInvalidRef.current) {
      const errorMessage = validator?.(values);
      const isInvalid = !!errorMessage;
      isInvalidRef.current = isInvalid;

      return {
        isInvalid,
        errorMessage,
      };
    }
    return {
      isInvalid: false,
    };
  }, [values, validator, validate]);

  // When the component goes to be unmounted, we will cancel fetching data.
  useEffect(
    () => () => {
      fetchItems.cancel();
    },
    [fetchItems],
  );

  return {
    ...rest,
    isInvalid: validationResult.isInvalid || isExternalInvalid,
    errorMessage: validationResult.errorMessage || externalErrorMessage,
    name,
    values,
    value: values,
    setValues,
    items,
    isLoading,
    renderValue,
    renderItem,
    onInputChange,
    onChange,
    clear,
    refresh,
    validator,
  };
}
